import { Component, computed, Signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    Breadcrumb,
    BreadcrumbListComponent,
    buildFolderBreadcrumbs,
    TitledContainerComponent,
} from '@r1-compass-library/components';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RoutingService } from '@r1-compass-library/services/routing.service';
import { combineLatest, map } from 'rxjs';
import { provideComponentStore } from '@ngrx/component-store';
import { rxEffect } from 'ngxtension/rx-effect';
import { filterNil } from 'ngxtension/filter-nil';
import { LifecycleRulesetStore } from './store/lifecycle-ruleset.store';
import { LifecycleRulesetListStore } from './store/lifecycle-ruleset-list.store';
import { AppConstants } from '../app.constants';
import { DataMartLifecyclesTableStore } from './store/data-mart-lifecycles.store';
import { LifecycleRulesValidationService } from './lifecycle-rules-validation.service';
import { LifecycleRulesValidationStateService } from './lifecycle-rules-validation-state.service';
import {
    SyncValidationStateTopLevelComponent,
    SyncValidationStateService,
    LifecycleRulesSectionName,
} from '@r1-compass-library/features/validation-sync';
import { CompassRouteParent } from '@r1-compass-library/models';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';

@Component({
    selector: 'r1-compass-app-lifecycle-rules',
    imports: [CommonModule, TitledContainerComponent, RouterModule, BreadcrumbListComponent],
    providers: [
        provideComponentStore(LifecycleRulesetStore),
        provideComponentStore(LifecycleRulesetListStore),
        provideComponentStore(DataMartLifecyclesTableStore),
    ],
    template: `
        @let lifecycleRuleset = lifecycleRulesetStore.lifecycleRuleset();
        @if (showCloseButton() && lifecycleRuleset) {
            <div class="additional-lifecycle-rules-info-bar">
                <r1-compass-lib-breadcrumb-list [breadcrumbList]="breadcrumbs()" />
                <p [style.color]="lifecycleRuleset.enabled ? 'green' : 'red'">
                    {{ lifecycleRuleset.enabled ? 'Enabled' : 'Disabled' }}
                </p>
            </div>
        }
        <r1-compass-lib-titled-container
            [title]="containerTitle()"
            [showCloseButton]="showCloseButton()"
            (close)="onClosed()">
            <router-outlet />
        </r1-compass-lib-titled-container>
    `,
    styles: `
        .additional-lifecycle-rules-info-bar {
            display: flex;
            justify-content: space-between;
        }
        p {
            margin-top: 0;
        }
    `,
})
export class LifecycleRulesComponent
    implements SyncValidationStateTopLevelComponent<LifecycleRulesSectionName, LifecycleRuleset>
{
    protected readonly routingService = inject(RoutingService);
    readonly lifecycleRulesetStore = inject(LifecycleRulesetStore);
    readonly lifecycleRulesetListStore = inject(LifecycleRulesetListStore);
    readonly #route = inject(ActivatedRoute);
    readonly #router = inject(Router);
    readonly #validationService = inject(LifecycleRulesValidationService);
    readonly #validationStateService = inject(LifecycleRulesValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);

    constructor() {
        effect(
            () => {
                const assetId = this.routingService.assetId();
                if (assetId && assetId !== 'new' && !isNaN(Number(assetId))) {
                    const numId = Number(assetId);
                    if (this.lifecycleRulesetStore.lifecycleRuleset()?.id !== numId) {
                        this.lifecycleRulesetStore.getLifecycleRuleset(numId);
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                } else if (assetId === 'new') {
                    if (this.lifecycleRulesetStore.lifecycleRuleset() !== undefined) {
                        this.lifecycleRulesetStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(true);
                } else {
                    if (this.lifecycleRulesetStore.lifecycleRuleset() !== undefined) {
                        this.lifecycleRulesetStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                }
            },
            { debugName: 'sync lifecycle ruleset store with route assetId' }
        );

        effect(
            () => {
                const routeData = this.#route.snapshot.data as CompassRouteParent;
                const invalidSections = this.#validationStateService.invalidSections();

                this.registerInvalidSections(routeData, invalidSections);
            },
            { debugName: 'Register lifecycle ruleset `invalidSections` under given criteria key' }
        );
        effect(
            () => {
                const asset = this.lifecycleRulesetStore.lifecycleRuleset();
                const assetId = this.routingService.assetId();

                if (asset && asset.id === Number(assetId)) {
                    this.validateSectionsOnLoad(asset, this.#validationService, this.#validationStateService);
                }
            },
            { debugName: 'lifecycle ruleset validation (on load)' }
        );

        rxEffect(
            combineLatest([
                this.lifecycleRulesetStore.lifecycleRuleset$.pipe(
                    map(ruleset => ruleset?.folderId),
                    filterNil()
                ),
                this.lifecycleRulesetListStore.usersAvailableDivisions$,
                this.lifecycleRulesetListStore.selectedIamDivision$.pipe(filterNil()),
            ]).pipe(
                map(([folderId, usersAvailableDivisions, selectedIamDivision]) => ({
                    folderId,
                    usersAvailableDivisions,
                    selectedIamDivision,
                }))
            ),
            res => {
                this.lifecycleRulesetListStore.getCurrentFolder(res);
            }
        );
    }

    breadcrumbs: Signal<Breadcrumb[]> = computed(() => {
        const currentFolder = this.lifecycleRulesetListStore.vm().currentFolder;
        return buildFolderBreadcrumbs(currentFolder, `/${AppConstants.LifecycleRulesComponent.path}`, true);
    });

    registerInvalidSections(routeData: CompassRouteParent, invalidSections: LifecycleRulesSectionName[]) {
        this.#syncValidationStateService.registerInvalidSections(routeData, invalidSections);
    }

    validateSectionsOnLoad(
        ruleset: LifecycleRuleset | undefined,
        validationService: LifecycleRulesValidationService,
        validationStateService: LifecycleRulesValidationStateService
    ) {
        this.#syncValidationStateService.validateSectionsOnLoad(ruleset, validationService, validationStateService);
    }

    containerTitle: Signal<string> = computed(() => {
        if (!this.showCloseButton()) {
            return AppConstants.LifecycleRulesComponent.mainTitle;
        }

        const currentChildPath = this.routingService.currentRoutePieces().routeWithoutParams?.split('/').at(-1);
        const subTitle =
            AppConstants.LifecycleRulesComponent.children.find(child => child.path === currentChildPath)
                ?.containerTitle ?? '';

        if (this.routingService.assetId() === 'new') {
            return `New Lifecycle Ruleset - ${subTitle}`;
        }

        const ruleset = this.lifecycleRulesetStore.lifecycleRuleset();
        const entityName = ruleset?.name || 'Lifecycle Ruleset';
        return `${entityName} - ${subTitle}`;
    });

    showCloseButton: Signal<boolean> = computed(() => {
        return this.routingService.parentSegment() !== '';
    });

    onClosed() {
        const folderId = this.lifecycleRulesetStore.lifecycleRuleset()?.folderId ?? AppConstants.defaultRootFolderId;
        this.lifecycleRulesetStore.reset();
        this.#router.navigate(['.'], {
            relativeTo: this.#route,
            queryParams: { folderId: folderId },
        });
    }
}
